import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, Redirect, useLocation } from "react-router-dom";

import Button from "veneer/Button";
import Typography from "veneer/Typography";

import { useAppDispatch } from "cortex/ui/src/hooks";
import { createPromiseApiAction } from "cortex/ui/src/redux-store/api-sagas/actions";
import { RESET_PASSWORD } from "cortex/ui/src/redux-store/users/actions";
import { PasswordInput } from "cortex/ui/src/ui/form";
import Notification from "cortex/ui/src/ui/notification";
import * as QS from "cortex/ui/src/util/queryString";
import {
  type ResponseLike,
  getErrorMessage,
} from "cortex/ui/src/util/response";

import BaseLoginView from "./BaseLoginView";

type FormValues = {
  p1: string;
  p2: string;
};

type LocationState = {
  [key: string]: unknown;
};

export default function ResetPassword() {
  const dispatch = useAppDispatch();
  const location = useLocation<LocationState>();

  const parsed = QS.parse(location.search);
  const token = (parsed as any)?.token as string | undefined;

  const [form, setForm] = useState<FormValues>({ p1: "", p2: "" });
  const [passwordChanged, setPasswordChanged] = useState<boolean>(false);
  const [passwordMeta, setPasswordMeta] = useState({ valid: false, score: 0 });
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const onResetPassword = useCallback(
    (t: string, password: string, passwordRepeat: string) => {
      return createPromiseApiAction(
        dispatch,
        RESET_PASSWORD,
        [t, password, passwordRepeat],
        "",
      );
    },
    [dispatch],
  );

  const doResetPassword = useCallback(() => {
    if (!token) {
      return Promise.resolve();
    }

    const { p1, p2 } = form;

    return onResetPassword(token, p1, p2)
      .then(() => {
        if (isMountedRef.current) {
          setErrorMessage("");
          setPasswordChanged(true);
        }
      })
      .catch((resp: unknown) => {
        if (isMountedRef.current) {
          const message = getErrorMessage(resp as Error | ResponseLike | null);
          setErrorMessage(message || "Failed to reset password.");
        }
      })
      .then(() => {
        if (isMountedRef.current) {
          setSubmitted(false);
        }
      });
  }, [form, onResetPassword, token]);

  const submit = () => {
    if (submitted || passwordChanged) {
      return;
    }
    setSubmitted(true);
    void doResetPassword();
  };

  if (!token) {
    return <Redirect to="/login" />;
  }

  if (passwordChanged) {
    return (
      <BaseLoginView>
        <Typography align="center">
          Your password has been successfully changed
        </Typography>
        <Link to="/login">
          <Button fullWidth size="large">
            Return to Login
          </Button>
        </Link>
      </BaseLoginView>
    );
  }

  return (
    <BaseLoginView>
      <Typography align="center">Enter your new password</Typography>
      <Notification error={errorMessage} />
      <PasswordInput
        minLength={8}
        onChange={(next, meta) => {
          setForm({ p1: next.password, p2: next.retype ?? "" });
          setPasswordMeta(meta);
        }}
        passwordLabel="New Password"
        value={{ password: form.p1 ?? "", retype: form.p2 ?? "" }}
        withRetype
      />
      <Button
        disabled={submitted || !passwordMeta.valid}
        fullWidth
        loading={submitted}
        onClick={submit}
        size="large"
      >
        Set New Password
      </Button>
    </BaseLoginView>
  );
}
